/**
 * Cómo se lee un evento de auditoría en el listado.
 *
 * Porteado de `AuditLogPage` de `imprenta`, que resuelve dos cosas que acá
 * faltaban: la **etiqueta legible** del tipo de evento y el **resumen del
 * payload** de la columna Detalles.
 *
 * Sin el resumen hay que abrir el JSON de cada fila para saber qué pasó, que es
 * exactamente lo que una pantalla de auditoría no debería pedir.
 */

type Tono = "neutral" | "ok" | "aviso" | "error";

/** Lo que se muestra donde no hay nada que resumir. */
const VACIO = "—";

/** Un resumen más largo que esto rompe la fila. */
const MAXIMO = 60;

/**
 * Etiqueta y color de cada tipo de evento.
 *
 * Los que `imprenta` traduce van con **su texto literal**, incluido el
 * «Consulta» donde el código dice `presupuesto` — es como se llama en toda la
 * interfaz. Los que son propios de acá —precios, productos, pruebas, la cola de
 * facturación, los eventos de auth de ADR 0037— siguen el mismo criterio.
 *
 * El tono lo decide lo que el evento HACE: verde crea, ámbar cambia, rojo
 * borra o falla.
 */
const ETIQUETAS: Record<string, { texto: string; tono: Tono }> = {
  estado_cambio: { texto: "Cambio de Estado", tono: "aviso" },
  // El salto forzado de ADR 0098: rojo, como lo que rompe una regla.
  produccion_estado_forzado: { texto: "Estado Forzado", tono: "error" },
  asignacion_impresora: { texto: "Asignación Impresora", tono: "neutral" },
  asignacion_responsable: { texto: "Asignación Responsable", tono: "neutral" },

  arte_subido: { texto: "Arte Subido", tono: "ok" },
  arte_eliminado: { texto: "Arte Eliminado", tono: "error" },
  prueba_subida: { texto: "Prueba Subida", tono: "ok" },
  prueba_eliminada: { texto: "Prueba Eliminada", tono: "error" },

  permiso_modificado: { texto: "Permiso Modificado", tono: "aviso" },
  rol_asignado: { texto: "Rol Asignado", tono: "ok" },
  rol_removido: { texto: "Rol Removido", tono: "error" },
  rol_creado: { texto: "Rol Creado", tono: "ok" },
  rol_eliminado: { texto: "Rol Eliminado", tono: "error" },

  usuario_creado: { texto: "Usuario Creado", tono: "ok" },
  usuario_activado: { texto: "Usuario Activado", tono: "ok" },
  usuario_desactivado: { texto: "Usuario Desactivado", tono: "error" },
  usuario_modificado: { texto: "Usuario Modificado", tono: "aviso" },
  password_cambiado: { texto: "Contraseña Cambiada", tono: "aviso" },
  login_exitoso: { texto: "Ingreso", tono: "ok" },
  login_fallido: { texto: "Ingreso Fallido", tono: "error" },

  invitacion_enviada: { texto: "Invitación Enviada", tono: "neutral" },
  invitacion_aceptada: { texto: "Invitación Aceptada", tono: "ok" },
  invitacion_cancelada: { texto: "Invitación Cancelada", tono: "error" },

  presupuesto_creado: { texto: "Consulta Creada", tono: "ok" },
  presupuesto_modificado: { texto: "Consulta Modificada", tono: "aviso" },
  presupuesto_aprobado: { texto: "Consulta Aprobada", tono: "ok" },
  presupuesto_rechazado: { texto: "Consulta Rechazada", tono: "error" },
  presupuesto_convertido: { texto: "Consulta Convertida", tono: "neutral" },
  presupuesto_eliminado: { texto: "Consulta Eliminada", tono: "error" },

  produccion_creada: { texto: "Producción Creada", tono: "ok" },
  produccion_modificada: { texto: "Producción Modificada", tono: "aviso" },
  produccion_eliminada: { texto: "Producción Eliminada", tono: "error" },
  produccion_restaurada: { texto: "Producción Restaurada", tono: "ok" },

  horas_registradas: { texto: "Horas Registradas", tono: "neutral" },
  horas_modificadas: { texto: "Horas Modificadas", tono: "aviso" },
  horas_eliminadas: { texto: "Horas Eliminadas", tono: "error" },

  cliente_creado: { texto: "Cliente Creado", tono: "ok" },
  cliente_modificado: { texto: "Cliente Modificado", tono: "aviso" },
  cliente_eliminado: { texto: "Cliente Eliminado", tono: "error" },

  impresora_creada: { texto: "Impresora Creada", tono: "ok" },
  impresora_modificada: { texto: "Impresora Modificada", tono: "aviso" },
  impresora_desactivada: { texto: "Impresora Desactivada", tono: "error" },

  producto_creado: { texto: "Producto Creado", tono: "ok" },
  producto_modificado: { texto: "Producto Modificado", tono: "aviso" },
  producto_eliminado: { texto: "Producto Eliminado", tono: "error" },

  precio_creado: { texto: "Precio Creado", tono: "ok" },
  precio_modificado: { texto: "Precio Modificado", tono: "aviso" },
  precio_eliminado: { texto: "Precio Eliminado", tono: "error" },

  alerta_facturacion_creada: { texto: "A Facturar", tono: "neutral" },
  alerta_facturacion_en_proceso: { texto: "Facturación En Proceso", tono: "aviso" },
  alerta_facturacion_facturada: { texto: "Facturada", tono: "ok" },
  alerta_facturacion_cancelada: { texto: "Facturación Anulada", tono: "error" },
  alerta_facturacion_revertida: { texto: "Facturación Revertida", tono: "error" },
  alerta_facturacion_emitida: { texto: "Alerta De Facturación Emitida", tono: "neutral" },
  alerta_facturacion_no_facturable: { texto: "No Se Factura", tono: "aviso" },
  alerta_facturacion_reabierta: { texto: "Facturación Reabierta", tono: "aviso" },

  config_changed: { texto: "Configuración Cambiada", tono: "aviso" },
};

/**
 * Cómo se muestra el tipo de evento.
 *
 * Un tipo sin traducir devuelve **su código**, no un hueco: es lo que hace
 * `imprenta`, y el código dice más que nada. Que aparezca uno en crudo es la
 * señal de que falta agregarlo acá.
 */
export function etiquetaDeEvento(tipo: string): { texto: string; tono: Tono } {
  return ETIQUETAS[tipo] ?? { texto: tipo, tono: "neutral" };
}

/**
 * Cómo se llama cada tipo de entidad en la interfaz.
 *
 * En la base son códigos en minúscula y sin tildes —`invitacion`,
 * `produccion`, `precio_impresion`— porque así se escriben desde el worker. Sin
 * este mapa la columna Entidad los muestra **en crudo**, que es lo que hacía
 * hasta acá: `imprenta` los muestra en castellano.
 *
 * Cada nombre es el de la **pantalla** que gobierna esa entidad, no el de la
 * tabla — mismo criterio que `nombreDeRecurso()` para los permisos (ADR 0082):
 * quien busca «Consultas» tiene que encontrarlas por ese nombre.
 */
const NOMBRES_ENTIDAD: Record<string, string> = {
  usuario: "Usuario",
  rol: "Rol",
  invitacion: "Invitación",
  cliente: "Cliente",
  presupuesto: "Consulta",
  produccion: "Producción",
  producto: "Producto",
  precio_impresion: "Precio",
  impresora: "Impresora",
  partes_hora: "Parte de hora",
  alerta_facturacion: "Facturación",
  configuracion: "Configuración",
  feature_flag: "Feature flag",
  forma_pago: "Forma de pago",
  tipo_trabajo: "Tipo de trabajo",
  tipo_base: "Tipo de base",
  tipo_impresora: "Tipo de impresora",
  tipo_tamano_logo: "Tamaño de logo",
};

/**
 * Una entidad sin nombre devuelve **su código**, no un hueco — igual que
 * `etiquetaDeEvento`. Que aparezca uno en crudo es la señal de que falta
 * agregarlo acá, y el código dice más que un guion.
 */
export function nombreDeEntidad(tipo: string): string {
  return NOMBRES_ENTIDAD[tipo] ?? tipo;
}

function texto(valor: unknown): string | null {
  if (typeof valor === "string" && valor.trim() !== "") return valor.trim();
  if (typeof valor === "number") return String(valor);
  return null;
}

/** Los campos que cambiaron, vengan como lista o como objeto. */
function camposCambiados(valor: unknown): string[] {
  if (Array.isArray(valor)) return valor.filter((x): x is string => typeof x === "string");
  if (typeof valor === "object" && valor !== null) return Object.keys(valor);
  return [];
}

function recortar(valor: string): string {
  return valor.length > MAXIMO ? `${valor.slice(0, MAXIMO)}…` : valor;
}

/**
 * El resumen del payload que va en la columna Detalles.
 *
 * **No repite lo que ya dicen las otras columnas.** «modificado» está en
 * Evento y la entidad está en Entidad; lo que falta saber es QUÉ cambió, y ese
 * es el criterio de `getSummaryText` de allá.
 *
 * Si nada aplica devuelve `—` en vez de volcar el JSON: para eso está «Ver».
 */
export function resumenDelEvento(tipo: string, payload: unknown): string {
  if (typeof payload !== "object" || payload === null) return VACIO;
  const p = payload as Record<string, unknown>;
  if (Object.keys(p).length === 0) return VACIO;

  // La transición es el dato del evento, no un adorno.
  if (tipo === "estado_cambio") {
    const desde = texto(p.desde);
    const hacia = texto(p.hacia);
    if (desde && hacia) return `${desde} → ${hacia}`;
  }

  // El salto forzado: la transición, lo que salteó y el motivo, en ese orden
  // para que el recorte se lleve el motivo y no la transición (ADR 0098 B10).
  if (tipo === "produccion_estado_forzado") {
    const desde = texto(p.desde);
    const hacia = texto(p.hacia);
    if (desde && hacia) {
      const salteadas = Array.isArray(p.condicionesSalteadas)
        ? p.condicionesSalteadas.filter((x): x is string => typeof x === "string")
        : [];
      const partes = [`${desde} → ${hacia}`];
      if (salteadas.length > 0) partes.push(`salteó ${salteadas.join(", ")}`);
      const motivo = texto(p.motivo);
      if (motivo) partes.push(motivo);
      return recortar(partes.join(" · "));
    }
  }

  // La alerta anulada por un salto: de qué OT, y en qué estado la agarró.
  if (tipo === "alerta_facturacion_cancelada") {
    const numero = texto(p.numero);
    const desde = texto(p.desde);
    if (numero && desde) return `${numero} · estaba ${desde.replaceAll("_", " ")}`;
  }

  // Facturada: con qué número de factura se cerró (ADR 0099).
  if (tipo === "alerta_facturacion_facturada") {
    const numero = texto(p.numero);
    const factura = texto(p.facturaRef);
    if (numero && factura) return recortar(`${numero} · factura ${factura}`);
  }

  // Decidir que no se factura, o reabrir esa decisión: de qué OT y por qué (ADR 0099).
  if (tipo === "alerta_facturacion_no_facturable" || tipo === "alerta_facturacion_reabierta") {
    const numero = texto(p.numero);
    const motivo = texto(p.motivo);
    if (numero && motivo) return recortar(`${numero} · ${motivo}`);
  }

  // Revertir: qué factura anuló qué nota de crédito, y por qué (ADR 0099 B5).
  if (tipo === "alerta_facturacion_revertida") {
    const numero = texto(p.numero);
    const factura = texto(p.facturaRef);
    const nota = texto(p.notaCreditoRef);
    if (numero && factura && nota) {
      const motivo = texto(p.motivo);
      return recortar([`${numero} · ${factura} → ${nota}`, motivo].filter(Boolean).join(" · "));
    }
  }

  // Cuántas OTs salieron de la consulta.
  if (tipo === "presupuesto_convertido" && Array.isArray(p.ots)) {
    const n = p.ots.length;
    return n === 1 ? "1 OT creada" : `${n} OTs creadas`;
  }

  const partes: string[] = [];

  // El identificador que la persona reconoce, en orden de preferencia.
  const identificador =
    texto(p.numero) ??
    texto(p.razonSocial) ??
    texto(p.nombreArchivo) ??
    texto(p.nombre) ??
    texto(p.email) ??
    texto(p.rol);
  if (identificador) partes.push(identificador);

  if (tipo.startsWith("horas_")) {
    const horas = texto(p.horas) ?? texto(p.horasDespues);
    if (horas) partes.push(`${horas} hs`);
  }

  const cambios = camposCambiados(p.cambios);
  if (cambios.length === 1) partes.push(cambios[0] as string);
  else if (cambios.length > 1) partes.push(`${cambios.length} campos`);

  if (partes.length === 0) {
    const suelto = texto(p.producto) ?? texto(p.precio);
    if (suelto) return recortar(suelto);
    return VACIO;
  }

  // El producto acompaña al número de OT: «OT-2026-0207 · Taza».
  if (tipo.startsWith("produccion_") && cambios.length === 0) {
    const producto = texto(p.producto);
    if (producto) partes.push(producto);
  }

  return recortar(partes.join(" · "));
}
